/**
 * Transport legs for a booking group, rendered from `transport_assignments`
 * (GET /api/bookings/:id/transport). Legs are attached to the group's primary
 * booking at create time, but a leg can be added to any booking later, so the
 * section fetches every booking in the group and merges.
 *
 * This replaces the raw `{"transport":[…]}` JSON an earlier build stuffed
 * into `bookings.notes` — the same data, laid out for a person.
 */
import { listBookingTransport, listVehicles } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { TransportAssignment, TransportStatus, Vehicle } from '@/lib/types';
import { formatWhat3Words, what3wordsMapUrl } from '@/lib/what3words';
import { ArrowDownToLine, ArrowUpFromLine, MapPin, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const CARD = 'rounded-xl border border-border bg-surface';
const SECTION_HEADER = 'flex items-center gap-2 px-5 py-3.5 border-b border-border';
const SECTION_LABEL = 'text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';

const STATUS_LABEL: Record<TransportStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'On the road',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_CLASS: Record<TransportStatus, string> = {
  scheduled: 'bg-secondary text-foreground',
  in_progress: 'bg-primary/10 text-primary',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-secondary text-muted-foreground line-through',
};

function formatScheduled(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

interface Props {
  /** Every booking id in the group — legs are merged across them. */
  bookingIds: string[];
  currency: string;
  /** Anchor id for the mobile section chips (e.g. `section-transport`). */
  id?: string | undefined;
  className?: string | undefined;
}

export function BookingTransportSection({ bookingIds, currency, id, className }: Props) {
  const [legs, setLegs] = useState<TransportAssignment[] | null>(null);
  const [vehicles, setVehicles] = useState<Map<string, Vehicle>>(new Map());
  const [unavailable, setUnavailable] = useState(false);

  const key = bookingIds.join(',');
  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setLegs([]);
      return;
    }
    setLegs(null);
    setUnavailable(false);
    Promise.all(
      ids.map((bid) =>
        listBookingTransport(bid).then(
          (r) => r.assignments,
          () => null,
        ),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        // Every fetch failed (typically 403 without transport.view) — say so
        // rather than pretending there is no transport.
        if (results.every((r) => r === null)) {
          setUnavailable(true);
          setLegs([]);
          return;
        }
        const merged = results.flatMap((r) => r ?? []);
        merged.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
        setLegs(merged);
      })
      .catch(() => {
        if (!cancelled) setLegs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => {
    if (!legs || !legs.some((l) => l.vehicleId)) return;
    let cancelled = false;
    listVehicles()
      .then((r) => {
        if (!cancelled) setVehicles(new Map(r.vehicles.map((v) => [v.id, v])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [legs]);

  const totalFee = (legs ?? []).reduce(
    (s, l) => (l.status === 'cancelled' ? s : s + l.feeMinor),
    0,
  );

  return (
    <div id={id} className={`${CARD} ${className ?? ''}`} data-testid="booking-transport">
      <div className={SECTION_HEADER}>
        <Truck className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="font-heading text-sm font-semibold text-foreground">Transport</h3>
        {legs && legs.length > 0 && (
          <span className="num ml-auto text-xs text-muted-foreground">
            {formatPriceMinor(totalFee, currency)} total
          </span>
        )}
      </div>

      {legs === null ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">Loading transport…</p>
      ) : unavailable ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">
          Transport details are not available to your role.
        </p>
      ) : legs.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">
          No pickup or drop-off arranged for this booking.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {legs.map((leg) => {
            const isPickup = leg.direction === 'pickup';
            const Icon = isPickup ? ArrowUpFromLine : ArrowDownToLine;
            const vehicle = leg.vehicleId ? vehicles.get(leg.vehicleId) : undefined;
            return (
              <li
                key={leg.id}
                className="px-5 py-3"
                data-testid={`transport-leg-${leg.id}`}
                data-direction={leg.direction}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Icon className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
                    {isPickup ? 'Pickup' : 'Drop-off'}
                  </span>
                  <span className="num text-sm text-foreground">
                    {formatScheduled(leg.scheduledAt)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium ${STATUS_CLASS[leg.status]}`}
                  >
                    {STATUS_LABEL[leg.status] ?? leg.status}
                  </span>
                  <span className="num ml-auto text-sm text-foreground">
                    {leg.feeMinor > 0 ? formatPriceMinor(leg.feeMinor, currency) : 'No fee'}
                  </span>
                </div>

                <div className="mt-1.5 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  <div className="min-w-0">
                    <p className={SECTION_LABEL}>Address</p>
                    {leg.pickupAddress ? (
                      <p className="mt-0.5 break-words text-foreground">{leg.pickupAddress}</p>
                    ) : (
                      <p className="mt-0.5 text-muted-foreground">Not recorded</p>
                    )}
                    {leg.pickupWhat3words && (
                      <a
                        href={what3wordsMapUrl(leg.pickupWhat3words)}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 inline-flex items-center gap-1 font-mono text-xs text-blue-600 hover:underline"
                      >
                        <MapPin className="size-3" strokeWidth={1.75} />
                        {formatWhat3Words(leg.pickupWhat3words)}
                      </a>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className={SECTION_LABEL}>Vehicle & distance</p>
                    <p className="mt-0.5 text-foreground">
                      {vehicle ? vehicle.name : leg.vehicleId ? 'Assigned vehicle' : 'Unassigned'}
                      {leg.distanceMiles != null && (
                        <span className="num text-muted-foreground">
                          {' '}
                          · {leg.distanceMiles.toFixed(1)} miles (straight line)
                        </span>
                      )}
                    </p>
                  </div>
                  {leg.notes && (
                    <div className="min-w-0 sm:col-span-2">
                      <p className={SECTION_LABEL}>Driver notes</p>
                      <p className="mt-0.5 whitespace-pre-wrap text-foreground">{leg.notes}</p>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {legs && legs.length > 0 && (
        <div className="border-t border-border px-5 py-2.5">
          <Link to="/transport" className="text-xs font-medium text-blue-600 hover:underline">
            Open driver manifest
          </Link>
        </div>
      )}
    </div>
  );
}
